
  import { Vue, Component, Prop, Watch } from "vue-property-decorator";

  import GET_COMPANIES_NAME from "@/apollo/autocomplete-queries/GetCompaniesName.gql";

  import DropdownFilter from "@/components/filters/DropdownFilter.vue";

  import DropdownOption from "@/entities/DropdownOption";
  import { Query_RootContractorArgs } from "@/generated/graphql";
  import filterMultiselectItems from "@/helpers/multiselect/filterMultiselectItems";

  interface Company {
    id: string,
    business_name: string,
  }

  @Component({
    components: {
      DropdownFilter,
    },
    apollo: {
      companies: {
        query: GET_COMPANIES_NAME,
        variables() {
          return this.filterConditions;
        },
        update(data): DropdownOption {
          return data.companies.map(({ id, business_name }: Company): DropdownOption => ({
            id,
            name: business_name,
          }));
        },
        skip() {
          return !this.searchString;
        },
      },
    },
  })
  export default class CompaniesMultiselect extends Vue {
    @Prop({ type: Array, required: true }) value!: string[];
    @Prop({ type: Boolean, default: true }) withBorder!: boolean;
    @Prop({ type: Boolean, default: true }) multiple!: boolean;
    @Prop({ type: Boolean, default: false }) invalid!: boolean;
    @Prop({ type: Object, default: () => ({}) }) condition!: Object;

    companies: DropdownOption[] = [];
    searchString = "";
    chosenCompanies: DropdownOption[] = [];

    get filterConditions(): Query_RootContractorArgs {
      const limit = 5;
      const conditions: any[] = [];

      if (this.searchString) {
        conditions.push({
          business_name: {
            _ilike: `%${this.searchString}%`,
          },
        });
      }

      if (this.multiple && this.value.length) {
        conditions.push({
          id: { _nin: this.value },
        });
      }

      if (conditions.length === 0) {
        return {
          where: this.condition,
          limit,
        };
      }

      return {
        where: { _and: [...conditions, this.condition] },
        limit,
      };
    }

    @Watch('value', {immediate: true})
    async onCompaniesIdsChanged(): Promise<void> {
      const companiesIds = this.value;

      if (companiesIds.length === 0) {
        this.chosenCompanies = [];
      }

      const {
        newItemsIds: newCompaniesIds,
        previouslyChosenItems: previouslyChosenCompanies,
      } = filterMultiselectItems(companiesIds, this.chosenCompanies);

      if (newCompaniesIds.length === 0) {
        return;
      }

      const rawData = await this.$apollo.query({
        query: GET_COMPANIES_NAME,
        variables: {
          where: {
            id: {
              _in: newCompaniesIds,
            },
          },
        },
      })

      const newChosenCompanies = rawData.data.companies.map(({ id, business_name }: Company): DropdownOption => ({
        id,
        name: business_name,
      }));

      this.chosenCompanies = [...previouslyChosenCompanies, ...newChosenCompanies];
    }

    handleInput(companies: DropdownOption[]): void {
      this.chosenCompanies = companies;
      const companiesIds = companies.map(company => company.id);
      this.$emit("input", companiesIds);
    }
  }
